import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DonationQRCodePage = () => {
  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const qrRef = useRef(null);
  const bankRef = useRef(null);
  const sectionTitleRef = useRef(null);

  // Auto scroll to donation section when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      const donationSection = document.getElementById('donation-section');
      if (donationSection) {
        donationSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // Hero entrance
      gsap.from('.hero-content', {
        opacity: 0,
        y: 80,
        duration: 1.2,
        ease: 'power3.out',
      });

      // Section title
      gsap.from(sectionTitleRef.current, {
        scrollTrigger: {
          trigger: sectionTitleRef.current,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        y: 40,
        duration: 1,
        ease: 'power3.out',
      });

      // QR card slides in from left
      gsap.from(qrRef.current, {
        scrollTrigger: {
          trigger: qrRef.current,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        x: -80,
        duration: 1.1,
        ease: 'power3.out',
      });

      // Bank card slides in from right
      gsap.from(bankRef.current, {
        scrollTrigger: {
          trigger: bankRef.current,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        x: 80,
        duration: 1.1,
        delay: 0.15,
        ease: 'power3.out',
      });

      // Bank detail rows stagger
      gsap.from('.bank-detail-row', {
        scrollTrigger: {
          trigger: bankRef.current,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        x: 30,
        duration: 0.5,
        stagger: 0.12,
        ease: 'power2.out',
        delay: 0.3,
      });

      // QR image pop
      gsap.from('.qr-image', {
        scrollTrigger: {
          trigger: qrRef.current,
          start: 'top 90%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        scale: 0.7,
        duration: 0.9,
        ease: 'back.out(1.7)',
        delay: 0.2,
      });

    }, pageRef);

    return () => ctx.revert();
  }, []);

  const bankDetails = [
    { label: "Account Name", value: "SUBAI JAMIAT AHLE HADEES ANDHRA PRADESH", icon: "🏦" },
    { label: "Account Number", value: "925020001282210", icon: "🔢" },
    { label: "IFSC Code", value: "UTIB0003345", icon: "🏛️" },
    { label: "Bank Address", value: "AXIS BANK, KT ROAD, VIJAYAWADA", icon: "📍" },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-white">

      {/* Hero Section with Ayat */}
      <div ref={heroRef} className="relative min-h-[100vh] sm:min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-400/60 via-gray-800/85 to-slate-900 z-10"></div>
          <img
            src="images/zakat.jpeg"
            alt="Islamic Background"
            className="w-full h-full object-fill"
          />
        </div>

        {/* Hero Content */}
        <div className="hero-content relative z-20 min-h-[100vh] sm:min-h-screen flex items-center justify-center px-4 py-12 sm:py-16 md:py-20">
          <div className="text-center max-w-6xl mx-auto w-full">

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-3 px-2"
            >
              Support Our Cause
            </motion.h1>

            {/* Main Quranic Verse */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mb-8 sm:mb-10 md:mb-12 px-2"
            >
              <div className="rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto">
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-arabic text-white text-right mb-4 sm:mb-6 md:mb-8 leading-relaxed">
                  مَثَلُ الَّذِينَ يُنفِقُونَ أَمْوَالَهُمْ فِي سَبِيلِ اللَّهِ كَمَثَلِ <br /><br /> حَبَّةٍ أَنبَتَتْ سَبْعَ سَنَابِلَ
                  فِي كُلِّ سُنبُلَةٍ مِّائَةُ حَبَّةٍ
                </p>
                <div className="border-t-2 border-white/30 pt-4 sm:pt-6">
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-emerald-50 mb-3 sm:mb-4 leading-relaxed italic">
                    "The example of those who spend their wealth in the way of Allah is like a seed that grows seven spikes; in each spike is a hundred grains."
                  </p>
                  <p className="text-gray-500 font-bold text-sm sm:text-base md:text-lg lg:text-xl">
                    — Surah Al-Baqarah (2:261)
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white text-gray-900 font-bold text-base sm:text-lg md:text-xl rounded-full shadow-2xl hover:bg-gray-200 transition-all"
                onClick={() => document.getElementById('donation-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Donate Now ↓
              </motion.button>
            </motion.div>

          </div>
        </div>
      </div>

      {/* Donation Section */}
      <div
        id="donation-section"
        className="donation-section py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-gray-300 via-white to-gray-300"
      >
        <div className="max-w-7xl mx-auto">

          {/* Section Title — TOP */}
          <div ref={sectionTitleRef} className="text-center mb-8 sm:mb-10 md:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-800 uppercase mb-3 tracking-tight">
              Make a Donation
            </h2>
            <div className="w-16 sm:w-20 h-1 bg-gray-800 mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm sm:text-base md:text-lg max-w-xl mx-auto">
              Choose your preferred method to contribute to our noble cause
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 max-w-6xl mx-auto">

            {/* QR Code Card */}
            <div ref={qrRef}>
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 lg:p-12 relative overflow-hidden h-full">
                <div className="absolute -top-10 -right-10 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-gray-200 to-gray-200 rounded-full opacity-20"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-gray-300 via-white to-gray-500 rounded-full opacity-20"></div>

                <div className="relative z-10">
                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-emerald-900 mb-2 sm:mb-3">
                      Scan to Donate
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base md:text-lg">Quick and secure payment</p>
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className="bg-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8"
                  >
                    <div className="qr-image bg-white p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-xl inline-block mx-auto">
                      <img
                        src="images/maekazqr.png"
                        alt="Donation QR Code"
                        className="w-44 h-44 sm:w-52 sm:h-52 md:w-60 md:h-60 lg:w-72 lg:h-72 xl:w-80 xl:h-80 mx-auto object-contain"
                      />
                    </div>
                  </motion.div>

                  <div className="text-center">
                    <p className="text-gray-700 text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-6">
                      📱 Use your banking app to scan this QR code
                    </p>
                  </div>
                </div>
              </div>
            </div>

          {/* Bank Details Card */}
<div ref={bankRef}>
  <div className="bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 lg:p-12 text-white relative overflow-hidden h-full">
    <div className="relative z-10">
      <div className="text-center mb-6 sm:mb-8">
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
          Bank Transfer
        </h3>
        <p className="text-gray-400 text-sm sm:text-base md:text-lg">For direct bank transfers</p>
      </div>

      <div className="space-y-3 sm:space-y-4 md:space-y-5">
        {bankDetails.map((detail, index) => (
          <div
            key={index}
            className="bg-white/10 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border border-white/20 hover:bg-white/20 hover:translate-x-1 transition-all duration-300"
          >
            <p className="text-gray-400 text-xs sm:text-sm mb-1 font-medium">
              {detail.label}
            </p>
            <p className="text-white text-sm sm:text-base md:text-md font-bold break-all leading-snug">
              {detail.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
</div>

          </div>

          {/* Bottom Note */}
          <div className="mt-10 sm:mt-12 md:mt-16 text-center pb-16 sm:pb-24 md:pb-32">
            <div className="inline-block bg-white rounded-xl sm:rounded-2xl shadow-md px-6 sm:px-8 md:px-10 py-4 sm:py-5 border border-gray-100 max-w-xl mx-auto">
              <p className="text-gray-700 text-xs sm:text-sm md:text-base font-medium leading-relaxed">
                🤲 May Allah accept your donation and reward you abundantly. For any queries, please contact us.
              </p>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default DonationQRCodePage;

// working 24/03/24

// import React, { useEffect, useRef } from 'react';
// import { motion } from 'framer-motion';
// import gsap from 'gsap';
// import { ScrollTrigger } from 'gsap/ScrollTrigger';

// gsap.registerPlugin(ScrollTrigger);

// const DonationQRCodePage = () => {
//   const pageRef = useRef(null);
//   const heroRef = useRef(null);

//   useEffect(() => {
//     const ctx = gsap.context(() => {
//       gsap.from('.hero-content', {
//         opacity: 0,
//         y: 80,
//         duration: 1.2,
//         ease: 'power3.out',
//       });

//       gsap.from('.donation-section', {
//         scrollTrigger: {
//           trigger: '.donation-section',
//           start: 'top 70%',
//         },
//         y: 80,
//         opacity: 0,
//         duration: 1.2,
//       });
//     }, pageRef);

//     return () => ctx.revert();
//   }, []);

//   const bankDetails = [
//     { label: "Account Name", value: "SUBAI JAMIAT AHLE HADEES ANDHRA PRADESH", icon: "🏦" },
//      { label: "Account Number", value: "925020001282210", icon: "🔢" },
//     { label: "IFSC Code", value: "UTIB0003345", icon: "🏛️" },
//     { label: "Bank Address", value: "AXIS BANK, KT ROAD, VIJAYAWADA", icon: "📍" },
   
//   ];

//   return (
//     <div ref={pageRef} className="min-h-screen bg-white">
      
//       {/* Hero Section with Ayat */}
//       <div ref={heroRef} className="relative min-h-[100vh] sm:min-h-screen overflow-hidden">
//         {/* Background Image with Overlay */}
//         <div className="absolute inset-0">
//           <div className="absolute inset-0 bg-gradient-to-b from-gray-400/60 via-gray-800/85 to-slate-900 z-10"></div>
//           <img 
//             src="images/zakat.jpeg"
//             alt="Islamic Background"
//             className="w-full h-full object-fill"
//           />
//           {/* Islamic Pattern Overlay */}
//           {/* <div className="absolute inset-0 z-10 opacity-10" style={{
//             backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
//           }}></div> */}
//         </div>

//         {/* Hero Content */}
//         <div className="hero-content relative z-20 min-h-[100vh] sm:min-h-screen flex items-center justify-center px-4 py-12 sm:py-16 md:py-20">
//           <div className="text-center max-w-6xl mx-auto w-full">
        
//             <motion.h1
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 0.5 }}
//               className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-3 px-2"
//             >
//               Support Our Cause
//             </motion.h1>

//             {/* Main Quranic Verse */}
//          {/* Main Quranic Verse */}
// <motion.div
//   initial={{ opacity: 0, y: 30 }}
//   animate={{ opacity: 1, y: 0 }}
//   transition={{ duration: 0.8, delay: 0.7 }}
//   className="mb-8 sm:mb-10 md:mb-12 px-2"
// >
//   <div className="rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto">
//     <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-arabic text-white text-right mb-4 sm:mb-6 md:mb-8 leading-relaxed">
//       مَثَلُ الَّذِينَ يُنفِقُونَ أَمْوَالَهُمْ فِي سَبِيلِ اللَّهِ كَمَثَلِ <br /><br />  حَبَّةٍ أَنبَتَتْ سَبْعَ سَنَابِلَ
//       فِي كُلِّ سُنبُلَةٍ مِّائَةُ حَبَّةٍ
//     </p>
//     <div className="border-t-2 border-white/30 pt-4 sm:pt-6">
//       <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-emerald-50 mb-3 sm:mb-4 leading-relaxed italic">
//         "The example of those who spend their wealth in the way of Allah is like a seed that grows seven spikes; in each spike is a hundred grains."
//       </p>
//       <p className="text-gray-500 font-bold text-sm sm:text-base md:text-lg lg:text-xl">
//         — Surah Al-Baqarah (2:261)
//       </p>
//     </div>
//   </div>
// </motion.div>


//               {/* <div className="bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 border border-white/20 max-w-4xl mx-auto shadow-2xl">
//                 <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-arabic leading-relaxed sm:leading-loose text-white mb-4 sm:mb-6 md:mb-8 text-right">
//                   مَثَلُ الَّذِينَ يُنفِقُونَ أَمْوَالَهُمْ فِي سَبِيلِ اللَّهِ كَمَثَلِ حَبَّةٍ أَنبَتَتْ سَبْعَ سَنَابِلَ فِي كُلِّ سُنبُلَةٍ مِّائَةُ حَبَّةٍ
//                 </p>
//                 <div className="border-t-2 border-white/30 pt-4 sm:pt-6">
//                   <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-emerald-50 mb-3 sm:mb-4 leading-relaxed italic">
//                     "The example of those who spend their wealth in the way of Allah is like a seed that grows seven spikes; in each spike is a hundred grains."
//                   </p>
//                   <p className="text-emerald-200 font-bold text-sm sm:text-base md:text-lg lg:text-xl">
//                     — Surah Al-Baqarah (2:261)
//                   </p>
//                 </div>
//               </div> */}

//             <motion.div
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 0.9 }}
//             >
//               <motion.button
//                 whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}
//                 whileTap={{ scale: 0.95 }}
//                 className="px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-white text-gray-900 font-bold text-base sm:text-lg md:text-xl rounded-full shadow-2xl hover:bg-gray-200 transition-all"
//                 onClick={() => document.getElementById('donation-section').scrollIntoView({ behavior: 'smooth' })}
//               >
//                 Donate Now ↓
//               </motion.button>
//             </motion.div>
//           </div>
//         </div>
//       </div>

//       {/* Donation Section */}
//       <div id="donation-section" className="donation-section py-12 sm:py-16 md:pb-80 md:py-20 px-4 bg-gradient-to-b from-gray-300 via-white to-gray-300">
//         <div className="max-w-7xl mx-auto">
        
//           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 max-w-6xl mx-auto">
            
//             {/* QR Code Section */}
//             <motion.div
//               initial={{ opacity: 0, x: -50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               viewport={{ once: true }}
//               transition={{ duration: 0.8 }}
//               className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 lg:p-12 relative overflow-hidden"
//             >
//               <div className="absolute -top-10 -right-10 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-gray-200 to-gray-200 rounded-full opacity-20"></div>
//               <div className="absolute -bottom-10 -left-10 w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-gray-300 via-white to-gray-500 rounded-full opacity-20"></div>
              
//               <div className="relative z-10">
//                 <div className="text-center mb-6 sm:mb-8">
//                   <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-emerald-900 mb-2 sm:mb-3">
//                     Scan to Donate
//                   </h3>
//                   <p className="text-gray-600 text-base sm:text-lg">Quick and secure payment</p>
//                 </div>

//                 <motion.div
//                   whileHover={{ scale: 1.05 }}
//                   transition={{ type: 'spring', stiffness: 300 }}
//                   className="bg-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8"
//                 >
//                   <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-xl inline-block mx-auto">
//                     <img 
//                       src="images/maekazqr.png" 
//                       alt="Donation QR Code"
//                       className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-80 lg:h-80 mx-auto"
//                     />
//                   </div>
//                 </motion.div>

//                 <div className="text-center">
//                   <p className="text-gray-700 text-sm sm:text-base md:text-lg mb-4 sm:mb-6">
//                     📱 Use your banking app to scan this QR code
//                   </p>
//                   {/* <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
//                     <div className="bg-emerald-100 text-emerald-800 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold">
//                       ✓ Secure
//                     </div>
//                     <div className="bg-emerald-100 text-emerald-800 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold">
//                       ✓ Instant
//                     </div>
//                   </div> */}
//                 </div>
//               </div>
//             </motion.div>

//             {/* Bank Details Section */}
//             <motion.div
//               initial={{ opacity: 0, x: 50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               viewport={{ once: true }}
//               transition={{ duration: 0.8 }}
//               className="bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 lg:p-12 text-white relative overflow-hidden"
//             >
//               {/* <div className="absolute inset-0 opacity-10" style={{
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
//               }}></div> */}

//               <div className="relative z-10">
//                 <div className="text-center mb-6 sm:mb-8">
//                   <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
//                     Bank Transfer
//                   </h3>
//                   <p className="text-gray-400 text-base sm:text-lg">For direct bank transfers</p>
//                 </div>

//                 <div className="space-y-4 sm:space-y-5">
//                   {bankDetails.map((detail, index) => (
//                     <motion.div
//                       key={index}
//                       initial={{ opacity: 0, x: -20 }}
//                       whileInView={{ opacity: 1, x: 0 }}
//                       viewport={{ once: true }}
//                       transition={{ delay: index * 0.1 }}
//                       whileHover={{ x: 5 }}
//                       className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-5 border border-white/20 hover:bg-white/20 transition-all"
//                     >
//                       <div className="flex items-start gap-3 sm:gap-4">
//                         {/* <span className="text-2xl sm:text-3xl flex-shrink-0">{detail.icon}</span> */}
//                         <div className="flex-1 min-w-0">
//                           <p className="text-gray-400 text-xs sm:text-sm mb-1 font-medium">
//                             {detail.label}
//                           </p>
//                           <p className="text-white text-base sm:text-lg md:text-xl font-bold break-words">
//                             {detail.value}
//                           </p>
//                         </div>
//                       </div>
//                     </motion.div>
//                   ))}
//                 </div>
//               </div>
//             </motion.div>

//           </div>
//         </div>
//       </div>

//     </div>
//   );
// };

// export default DonationQRCodePage;